// React
import React, { useEffect, useState } from "react";

// MUI
import { styled, Tab, Tabs, TabScrollButton } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Themes

// Components
import {
  copyLinkItem,
  openInNewTabItem,
  openItem,
  useContextMenu,
} from "components/ui/ContextMenu";

// Hooks
import useUpdateOperatorStatus from "hooks/operators/useUpdateOperatorStatus";

// Utils
import { colors } from "styles/tokens";

// Styled components
const StyledTabs = styled(Tabs)({
  minHeight: 44,
  "& .MuiTabs-indicator": {
    backgroundColor: colors.primary,
    height: 2,
    borderRadius: 2,
  },
  "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
});

const StyledTab = styled(Tab)({
  minHeight: 44,
  minWidth: 0,
  padding: 0,
  textTransform: "none",
  color: colors.gray500,
  fontSize: 14,
  fontWeight: 500,
  "&:hover": { color: colors.gray900 },
  "&.Mui-selected": { color: colors.gray900 },
  "&.Mui-selected .tab-icon": { color: colors.primary },
  "&.MuiTab-root": { opacity: 1 },
  "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: -2 },
});

const TabLink = styled("a")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "0 12px",
  minHeight: 44,
  textDecoration: "none",
  color: "inherit",
  width: "100%",
  height: "100%",
  whiteSpace: "nowrap",
  "&:hover": { textDecoration: "none", color: "inherit" },
  "&:visited": { color: "inherit" },
  "&:focus-visible": { outline: "none" },
});

// Scroll arrows with a label for screen readers
const ScrollButton = React.forwardRef(function ScrollButton(props, ref) {
  const { t } = useTranslation();
  return (
    <TabScrollButton
      {...props}
      ref={ref}
      aria-label={
        props.direction === "left" ? t("shell:tabs.scrollLeft") : t("shell:tabs.scrollRight")
      }
    />
  );
});

// "New" marker on a tab: readable pill, not a 7px dot of text
const NewBadge = ({ children }) => (
  <span className="rounded-full bg-primary-strong px-1.5 py-px text-[11px] leading-4 font-semibold text-white">
    {children}
  </span>
);

// A real in-app path can be opened elsewhere; "#hash" tabs only change the view of this page
const isOpenablePath = (path) =>
  typeof path === "string" && path.length > 0 && !path.startsWith("#");

/**
 * HeaderNavigationTabs component
 *
 * Tabs are real links (middle click, Cmd/Ctrl+click and the right-click menu open them in a new
 * tab). The bar scrolls sideways when the tabs do not fit.
 *
 * @param {Object} props
 * @param {string} props.title - The title to display in the header
 * @param {Array} props.tabs - An array of tab objects with { name, icon, path }
 * @param {number} props.activeTab - Index of the active tab (optional)
 * @param {function} props.onTabChange - Callback for tab change (optional)
 * @param {React.ReactNode} props.titleExtra - Shown next to the title (optional)
 * @param {React.ReactNode} props.actions - Right of the tabs (search, buttons) (optional)
 * The bar is a plain block in the normal flow (title, tabs, bottom border) inside the page
 * padding: one row from sm, below sm the title on top and the tabs, scrollable, under it.
 * Tab icons are sized here (20px): callers pass the bare icon. `inline` is still accepted for
 * the existing callers; the old full-bleed variant, which overflowed its parent with a negative
 * width and a transform, is gone.
 */
const HeaderNavigationTabs = ({
  title,
  tabs,
  activeTab = 0,
  onTabChange,
  customStyle,
  titleExtra = null,
  actions = null,
}) => {
  const { t } = useTranslation();
  const menu = useContextMenu();
  const [selectedTab, setSelectedTab] = useState(activeTab);
  const navigate = useNavigate();
  const operatorId = JSON.parse(localStorage.getItem("operator_id")) || null;
  const hasReadLatestAnnouncement = useSelector(
    (state) => state.profile.user?.has_read_latest_announcement
  );

  const { updateOperatorAnnouncement } = useUpdateOperatorStatus();

  // Follow the parent (browser back, links, deep links)
  useEffect(() => {
    setSelectedTab(activeTab);
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (onTabChange) {
      onTabChange(newValue, tabs[newValue]);
    }
  };

  // Same effect as a plain click on the tab
  const activateTab = (index, tab) => {
    if (tab.hasBadge && !hasReadLatestAnnouncement && operatorId) {
      updateOperatorAnnouncement(operatorId, true);
    }
    setSelectedTab(index);
    if (onTabChange) {
      onTabChange(index, tab);
    } else if (tab.path) {
      // Fallback navigation if no onTabChange handler
      navigate(tab.path);
    }
  };

  const handleLinkClick = (event, index, tab) => {
    // Check if it's a middle click, ctrl+click, or cmd+click (should open in new tab)
    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      return; // Let the browser handle opening in new tab
    }
    // Prevent default link behavior for normal clicks
    event.preventDefault();
    activateTab(index, tab);
  };

  const tabMenu = (index, tab) =>
    isOpenablePath(tab.path)
      ? menu.bind(() => [
          openItem(() => activateTab(index, tab)),
          openInNewTabItem(tab.path),
          copyLinkItem(tab.path),
        ])
      : {};

  const tabsElement = (
    <StyledTabs
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      slots={{ scrollButtons: ScrollButton }}
      value={selectedTab >= 0 && selectedTab < tabs.length ? selectedTab : false}
      onChange={handleTabChange}
      aria-label={t("navigation:headerNavigationTabs.ariaLabel")}
      sx={{ flex: 1, minWidth: 0 }}
    >
      {tabs.map((tab, index) => (
        <StyledTab
          key={tab.name}
          label={
            <TabLink
              href={tab.path}
              onClick={(e) => handleLinkClick(e, index, tab)}
              onMouseDown={(e) => {
                // Handle middle click
                if (e.button === 1) {
                  e.preventDefault();
                }
              }}
              {...tabMenu(index, tab)}
            >
              {tab.icon && (
                <span
                  className="tab-icon inline-flex shrink-0 items-center [&>svg]:size-5"
                  aria-hidden="true"
                >
                  {tab.icon}
                </span>
              )}
              <span>{tab.name}</span>
              {tab.hasBadge && !hasReadLatestAnnouncement && (
                <NewBadge>{t("navigation:headerNavigationTabs.newBadge")}</NewBadge>
              )}
            </TabLink>
          }
          id={`nav-tab-${index}`}
          aria-controls={`nav-tabpanel-${index}`}
        />
      ))}
    </StyledTabs>
  );

  return (
    <div
      className="flex min-w-0 shrink-0 flex-col gap-1 border-b border-gray-200 sm:flex-row sm:items-center sm:gap-4"
      style={customStyle}
    >
      {title && (
        <div className="flex shrink-0 items-center">
          <h1 className="page-title m-0">{title}</h1>
          {titleExtra}
        </div>
      )}
      {tabsElement}
      {actions && <div className="flex shrink-0 items-center gap-2 pl-2">{actions}</div>}
    </div>
  );
};

export default HeaderNavigationTabs;
